import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Modal, TextInput, Skeleton, Stack, Text, Group } from '@mantine/core';
import {
  IconMessageCircle,
  IconUser,
  IconList,
  IconCalendar,
  IconBuilding,
  IconTarget,
  IconTicket,
  IconTag,
  IconArticle,
  IconSearch,
  IconCards,
  IconInfoCircle,
} from '@tabler/icons';
import { ICON_MAP } from '../../../constants/constants';
import {ReusableFilterModalProps, FilterCategory, ActiveFilter, FilterConfig, AdditionalConfiguration} from './types';
import {
  getActiveFilterCountForCategory,
  getChildFilterActiveCount,
  getFilterKey,
  shouldShowFilterInInbox,
  FILTERS_TO_HIDE,
} from './utils';
import './FilterModal.css';
import _ from "lodash";
import FilterModalContainer from './components/FilterModalContainer';
import FilterModalHeader from './components/FilterModalHeader';
import SavedFilters from './components/applied-filters/SavedFilters';
import { useUpdateFiltersFromModal } from '../../../hooks/useCustomizations';
import { useQueryClient } from '@tanstack/react-query';
import ManageFiltersSelection from './manage-filters/ManageFiltersSelection';
import ManageFilterModalContainer from './manage-filters/ManageFiltersModalContainer';

const HIDDEN_FROM_MANAGE_IDS = new Set(['evidence-owner', 'evidence-product-area', 'owner', 'product-area']);

const ReusableFilterModal: React.FC<ReusableFilterModalProps> = ({
  opened,
  onClose,
  filtersConfig,
  activeFilters,
  setActiveFilters,
  filterObject = 'both',
  categoryFilter,
  disabledCategories = [],
  onFiltersSaved,
  onClearFilters,
  showIdeaAttributes = true,
  additionalConfigurations = [],
  initialSelectedCategory,
  selectedView,
  isFromIdeas = false,
}) => {


  const { mutate: updateFiltersFromModal } = useUpdateFiltersFromModal();
  const queryClient = useQueryClient();

  // Local filter state - working copy until save
  const [localActiveFilters, setLocalActiveFilters] = useState<ActiveFilter[]>([]);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedChildFilter, setSelectedChildFilter] = useState<string | null>(null);
  // Track what's displayed in the right panel (persists when clicking on parents)
  const [displayedCategory, setDisplayedCategory] = useState<string>('');
  const [displayedChildFilter, setDisplayedChildFilter] = useState<string | null>(null);
  const [displayedCategoryType, setDisplayedCategoryType] = useState<'idea' | 'evidence' | null>(null);
  const [activeOptions, setActiveOptions] = useState<{ [key: string]: any[] }>({});
  const [fetchableCheckboxCounts, setFetchableCheckboxCounts] = useState<{ [key: string]: number }>({});
  const [additionalConfigurationsValues, setAdditionalConfigurationsValues] = useState<{ [key: string]: any }>({})

  // Search states
  const [middlePanelSearch, setMiddlePanelSearch] = useState<string>('');
  const [rightPanelSearch, setRightPanelSearch] = useState<string>('');

  // Clear trigger for child filters
  const [clearTrigger, setClearTrigger] = useState<number>(0);

  const [isManagingFilters, setIsManagingFilters] = useState<boolean>(false);
  const [isSavingFilters, setIsSavingFilters] = useState<boolean>(false);
  const [filterVisibility, setFilterVisibility] = useState<Record<string, boolean>>({});
  const filterVisibilityRef = useRef<Record<string, boolean>>({});
  // Track previous opened state to detect when modal transitions from closed to open
  const prevOpenedRef = useRef<boolean>(false);
  const prevIsManagingRef = useRef<boolean>(false);

  const getKeysToInclude = useCallback((section: Record<string, any> | undefined): Set<string> => {
    if (!section) return new Set();
    const labelToKey = new Map<string, string>();
    Object.entries(section).forEach(([key, config]) => {
      if (key === '_id') return;
      const label = (config as any).label || key;
      if (!labelToKey.has(label)) labelToKey.set(label, key);
    });
    return new Set(labelToKey.values());
  }, []);

  const labelSiblings = useMemo(() => {
    const siblings = new Map<string, string[]>();
    const buildFromSection = (section: Record<string, any> | undefined) => {
      if (!section) return;
      const labelToKeys = new Map<string, string[]>();
      Object.entries(section).forEach(([key, config]) => {
        if (key === '_id') return;
        const label = (config as any).label || key;
        if (!labelToKeys.has(label)) labelToKeys.set(label, []);
        labelToKeys.get(label)!.push(key);
      });
      labelToKeys.forEach((keys) => {
        if (keys.length > 1) {
          keys.forEach(k => siblings.set(k, keys));
        }
      });
    };
    buildFromSection(filtersConfig?.idea);
    buildFromSection(filtersConfig?.evidence);
    return siblings;
  }, [filtersConfig]);

  useEffect(() => {
    if (isManagingFilters && !prevIsManagingRef.current && filtersConfig) {
      const visibility: Record<string, boolean> = {};
      const isVisible = (config: any) =>
        isFromIdeas ? !config.hidden : config.showInInbox !== false;
      const processSection = (section: Record<string, FilterConfig> | undefined) => {
        if (!section) return;
        const seenLabels = new Set<string>();
        Object.entries(section).forEach(([key, config]) => {
          if (key === '_id') return;
          const label = (config as any).label || key;
          if (seenLabels.has(label)) return;
          seenLabels.add(label);
          const visible = isVisible(config);
          const siblings = labelSiblings.get(key);
          if (siblings) {
            siblings.forEach(k => { visibility[k] = visible; });
          } else {
            visibility[key] = visible;
          }
          if ((config as FilterConfig).childFilters) {
            Object.entries((config as FilterConfig).childFilters!).forEach(([childKey, childConfig]) => {
              visibility[`${key}.${childKey}`] = isVisible(childConfig);
            });
          }
        });
      };
      processSection(filtersConfig?.idea);
      processSection(filtersConfig?.evidence);
      setFilterVisibility(visibility);
      filterVisibilityRef.current = visibility;
    }
    prevIsManagingRef.current = isManagingFilters;
  }, [isManagingFilters, filtersConfig, isFromIdeas, labelSiblings]);

  const handleToggleVisibility = useCallback((filterId: string) => {
    setFilterVisibility(prev => {
      const newValue = !prev[filterId];
      const next = { ...prev, [filterId]: newValue };
      const siblings = labelSiblings.get(filterId);
      if (siblings) {
        siblings.forEach(k => { next[k] = newValue; });
      }
      filterVisibilityRef.current = next;
      return next;
    });
  }, [labelSiblings]);

  const editFilters = () => {
    const vis = filterVisibilityRef.current;
    const buildSectionPayload = (section: Record<string, FilterConfig> | undefined) => {
      if (!section) return {};
      const validKeys = getKeysToInclude(section);
      const result: Record<string, any> = {};
      Object.entries(section).forEach(([key, config]) => {
        if (key === '_id' || !validKeys.has(key)) return;
        const visible = vis[key] !== false;
        const entry: any = {
          name: key,
          ...(isFromIdeas ? { hidden: !visible } : { showInInbox: visible }),
        };
        if (config.childFilters) {
          entry.childFilters = {};
          Object.keys(config.childFilters).forEach((childKey) => {
            const childVisible = vis[`${key}.${childKey}`] !== false;
            entry.childFilters[childKey] = {
              name: childKey,
              ...(isFromIdeas ? { hidden: !childVisible } : { showInInbox: childVisible }),
            };
          });
        }
        result[key] = entry;
      });
      return result;
    };

    setIsSavingFilters(true);
    setIsManagingFilters(false);
    updateFiltersFromModal(
      {
        component: isFromIdeas ? 'fromIdeas' : 'inbox',
        idea: buildSectionPayload(filtersConfig?.idea),
        evidence: buildSectionPayload(filtersConfig?.evidence),
      },
      {
        onSuccess: () => {
          queryClient.refetchQueries({ queryKey: ["ideasFiltersConfig"] }).then(() => {
            setIsSavingFilters(false);
          });
        },
        onError: () => {
          setIsSavingFilters(false);
        },
      }
    );
  }

  const getNormalizedCategories = (filtersConfigData: any, type: 'idea' | 'evidence', currentSelectedCategory: string, currentLocalActiveFilters: ActiveFilter[]): FilterCategory[] => {
    // Filter categories by showInInbox flag
    const categories: FilterCategory[] = [];
    const itemCategoriesFiltered: { [key: string]: FilterConfig } = {};
    const itemCategories = filtersConfigData[type] || {};

    Object.entries(itemCategories).forEach(([key, category]: [string, any]) => {
      // Apply showInInbox filtering for evidence filters
      const shouldShow = shouldShowFilterInInbox(category as FilterConfig, key, filterObject);

      // Also apply custom categoryFilter if provided
      const passesCustomFilter = !categoryFilter || categoryFilter(category as FilterConfig, key);

      if (shouldShow && passesCustomFilter && key !== '_id') {
        itemCategoriesFiltered[key] = category as FilterConfig;
      }
    });

    Object.entries(itemCategoriesFiltered).forEach(([key, category]) => {
      categories.push({
        id: key,
        label: category.label || key,
        active: currentSelectedCategory === key,
        filterCount: getActiveFilterCountForCategory(key, currentLocalActiveFilters, type),
        disabled: disabledCategories.includes(key),
        categoryType: type
      });
    });

    return categories;
  }

  // Get available categories based on filterObject and categoryFilter.
  // Categories are assigned to Ideas vs Evidence section by each filter's filterObject property,
  // not by which config key (idea/evidence) they came from, so config aligns with UI.
  const getAvailableCategoriesHelper = (filtersConfigData: any, currentSelectedCategory: string, currentLocalActiveFilters: ActiveFilter[], skipVisibilityFilter = false): FilterCategory[] => {
    if (!filtersConfigData) return [];

    const allCategories: FilterCategory[] = [];
    const addedIds = new Set<string>();

    const addCategory = (key: string, category: FilterConfig, section: 'idea' | 'evidence') => {
      if (addedIds.has(key)) return;
      addedIds.add(key);
      allCategories.push({
        id: key,
        label: category.label || key,
        active: currentSelectedCategory === key,
        filterCount: getActiveFilterCountForCategory(key, currentLocalActiveFilters, section),
        disabled: disabledCategories.includes(key),
        categoryType: section,
      });
    };

    const SHARED_FILTER_KEYS = new Set(['owner', 'product-area']);

    const deduplicateCategories = (categories: FilterCategory[], configData: any): FilterCategory[] => {
      const byId = new Map<string, FilterCategory>();
      categories.forEach(cat => byId.set(cat.id, cat));
      const deduped = Array.from(byId.values());

      const seenDbFieldPaths = new Map<string, string>();
      const seenLabels = new Map<string, string>();
      const idsToRemove = new Set<string>();

      deduped.forEach(cat => {
        const config =
          configData?.idea?.[cat.id] || configData?.evidence?.[cat.id];
        const dbFieldPath = config?.dbFieldPath;

        if (dbFieldPath) {
          const existing = seenDbFieldPaths.get(dbFieldPath);
          if (existing) {
            const keepCanonical = SHARED_FILTER_KEYS.has(existing);
            idsToRemove.add(keepCanonical ? cat.id : existing);
            if (!keepCanonical) seenDbFieldPaths.set(dbFieldPath, cat.id);
          } else {
            seenDbFieldPaths.set(dbFieldPath, cat.id);
          }
        }

        const label = cat.label?.toLowerCase();
        if (label) {
          const existingByLabel = seenLabels.get(label);
          if (existingByLabel && !idsToRemove.has(cat.id)) {
            const keepCanonical = SHARED_FILTER_KEYS.has(existingByLabel);
            idsToRemove.add(keepCanonical ? cat.id : existingByLabel);
            if (!keepCanonical) seenLabels.set(label, cat.id);
          } else if (!existingByLabel) {
            seenLabels.set(label, cat.id);
          }
        }
      });

      return deduped.filter(cat => !idsToRemove.has(cat.id));
    };

    const processConfig = (configSection: Record<string, any>, defaultSection: 'idea' | 'evidence') => {
      Object.entries(configSection || {}).forEach(([key, category]: [string, any]) => {
        if (key === '_id') return;

        // When modal is evidence-only, skip shared filters from the idea config
        // so the evidence config's version (with correct categoryType) is used instead.
        if (filterObject === 'evidence' && defaultSection === 'idea' && SHARED_FILTER_KEYS.has(key)) {
          return;
        }

        const shouldShow = skipVisibilityFilter || shouldShowFilterInInbox(category as FilterConfig, key, filterObject);
        const passesCustom = !categoryFilter || categoryFilter(category as FilterConfig, key);
        if (shouldShow && passesCustom) {
          let section: 'idea' | 'evidence' =
            category.filterObject === 'idea' || category.filterObject === 'evidence'
              ? category.filterObject
              : defaultSection;
          // Owner and product-area live under evidence config (often with filterObject: 'evidence') but belong in the Idea filters group on the Ideas page.
          if (
            (filterObject === 'idea' || filterObject === 'both') &&
            SHARED_FILTER_KEYS.has(key)
          ) {
            section = 'idea';
          }
          // When evidence-only modal, force shared filters to evidence section
          if (filterObject === 'evidence' && SHARED_FILTER_KEYS.has(key)) {
            section = 'evidence';
          }
          addCategory(key, category as FilterConfig, section);
        }
      });
    };

    processConfig(filtersConfigData.idea || {}, 'idea');
    processConfig(filtersConfigData.evidence || {}, 'evidence');

    const ideaCategories = deduplicateCategories(allCategories.filter(c => c.categoryType === 'idea'), filtersConfigData);
    const evidenceCategories = deduplicateCategories(allCategories.filter(c => c.categoryType === 'evidence'), filtersConfigData);

    // Return sections based on modal filterObject
    if (filterObject === 'idea') return ideaCategories;
    if (filterObject === 'evidence') return evidenceCategories;
    return [...ideaCategories, ...evidenceCategories];
  };

  // Initialize local filters when modal opens (only when opened changes from false to true)
  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      // Modal just opened - initialize everything
      // Deep copy activeFilters to avoid reference issues, filtering out undefined selected
      const deepCopiedFilters = activeFilters
        .filter(filter => filter.selected !== undefined)
        .map(filter => ({
          ...filter,
          selected: Array.isArray(filter.selected) 
            ? [...filter.selected] 
            : filter.selected, // Keep non-array values (boolean, string, number, etc.) as-is
          options: filter.options ? { ...filter.options } : {}
        }));
      setLocalActiveFilters(deepCopiedFilters);

      // Don't select any filter when opening the modal
      setSelectedCategory('');
      setSelectedChildFilter(null);
      setDisplayedCategory('');
      setDisplayedChildFilter(null);
      setDisplayedCategoryType(null);

      // Initialize activeOptions from activeFilters
      const newActiveOptions: { [key: string]: any[] } = {};
      activeFilters.forEach((filter: ActiveFilter) => {
        if (filter.selected && Array.isArray(filter.selected) && filter.selected.length > 0) {
          const key = getFilterKey(filter.filterObject, filter.name);
          newActiveOptions[key] = [...filter.selected];
        }
      });
      setActiveOptions(newActiveOptions);

      if (_.size(additionalConfigurations)) {
        // Initialize additionalConfigurationsValues
        const newAdditionalConfigurationsValues: { [key: string]: any } = {};
        additionalConfigurations.forEach((config: AdditionalConfiguration) => {
          newAdditionalConfigurationsValues[config.id] = config.value;
        });
        setAdditionalConfigurationsValues(newAdditionalConfigurationsValues);
      }

      setMiddlePanelSearch('');
      setRightPanelSearch('');
    }
    
    // Update prevOpenedRef
    prevOpenedRef.current = opened;
  }, [opened, additionalConfigurations]);

  // Update local filters when activeFilters or filtersConfig changes while modal is open
  // But don't reset the selected category
  useEffect(() => {
    if (opened && prevOpenedRef.current) {
      // Modal is already open - only update filters, don't reset category
      // Filter out undefined selected values
      const deepCopiedFilters = activeFilters
        .filter(filter => filter.selected !== undefined)
        .map(filter => ({
          ...filter,
          selected: Array.isArray(filter.selected) 
            ? [...filter.selected] 
            : filter.selected, // Keep non-array values (boolean, string, number, etc.) as-is
          options: filter.options ? { ...filter.options } : {}
        }));
      setLocalActiveFilters(deepCopiedFilters);

      // Update activeOptions from activeFilters
      const newActiveOptions: { [key: string]: any[] } = {};
      activeFilters.forEach((filter: ActiveFilter) => {
        if (filter.selected && Array.isArray(filter.selected) && filter.selected.length > 0) {
          const key = getFilterKey(filter.filterObject, filter.name);
          newActiveOptions[key] = [...filter.selected];
        }
      });
      setActiveOptions(newActiveOptions);
    }
  }, [activeFilters, filtersConfig]);

  // When selectedCategory changes, clear selectedChildFilter so user must explicitly click a child
  useEffect(() => {
    if (opened && selectedCategory && filtersConfig) {
      let categoryData = filtersConfig.idea?.[selectedCategory];
      if (!categoryData) {
        categoryData = filtersConfig.evidence?.[selectedCategory];
      }

      // Do not auto-select any child; show child in RightPanel only when user clicks a child in LeftPanel
      if (categoryData?.childFilters && Object.keys(categoryData.childFilters).length > 0) {
        setSelectedChildFilter(null);
      } else if (categoryData?.options && Object.keys(categoryData.options).length > 0) {
        setSelectedChildFilter(null);
      } else if (_.isObject(categoryData)) {
        setSelectedChildFilter(null);
      } else {
        setSelectedChildFilter(null);
      }
    }
  }, [opened, selectedCategory, filtersConfig]);

  // Get available categories based on filterObject and categoryFilter
  const getAvailableCategories = (): FilterCategory[] => {
    return getAvailableCategoriesHelper(filtersConfig, selectedCategory, localActiveFilters);
  };

  const availableCategories = useMemo(() => getAvailableCategories(), [
    filtersConfig,
    filterObject,
    categoryFilter,
    selectedCategory,
    localActiveFilters,
    disabledCategories,
    showIdeaAttributes,
  ]);

  const allAvailableCategories = useMemo(
    () => getAvailableCategoriesHelper(filtersConfig, selectedCategory, localActiveFilters, true),
    [filtersConfig, filterObject, categoryFilter, selectedCategory, localActiveFilters, disabledCategories, showIdeaAttributes]
  );

  // Get child filters for selected category
  const getChildFilters = () => {
    if (!selectedCategory || !filtersConfig) return [];

    let categoryData = filtersConfig.idea?.[selectedCategory];
    if (!categoryData) {
      categoryData = filtersConfig.evidence?.[selectedCategory];
    }

    if (!categoryData) return [];

    // Do not expand child filters when the category type is fetchableCheckbox
    if ((categoryData as FilterConfig)?.type === 'fetchableCheckbox' || (categoryData as FilterConfig)?.type === 'date') {
      return [];
    }

    // Only use actual childFilters from parent-type filters; never treat config keys as children
    const hasChildFilters =
      categoryData.childFilters &&
      _.isObject(categoryData.childFilters) &&
      Object.keys(categoryData.childFilters).length > 0;
    if (hasChildFilters) {
      return Object.entries(categoryData.childFilters)
        .filter(([key, childFilter]: [string, any]) => {
          if (_.includes(FILTERS_TO_HIDE, key)) {
            return false;
          }
          return _.has(childFilter, 'enabled') ? childFilter.enabled : true;
        })
        .map(([key, childFilter]: [string, any]) => ({
          id: key,
          label: childFilter.label || key,
          type: childFilter.type,
          active: selectedChildFilter === key,
          filterCount: getChildFilterActiveCount(
            key,
            selectedCategory,
            activeOptions,
            fetchableCheckboxCounts,
            localActiveFilters,
            filtersConfig
          ),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    // If it's a direct filter with no child filters, return empty array
    // Options will be rendered directly in the right panel
    return [];
  };

  const childFilters = useMemo(() => getChildFilters(), [
    selectedCategory,
    filtersConfig,
    selectedChildFilter,
    activeOptions,
    fetchableCheckboxCounts,
    localActiveFilters,
  ]);

  // Category ids that have child filters (for showing triangle icon)
  const categoryIdsWithChildren = useMemo(() => {
    const ids = new Set<string>();
    if (!filtersConfig) return ids;
    const check = (configSection: Record<string, any> | undefined) => {
      Object.keys(configSection || {}).forEach((key) => {
        const categoryData = configSection?.[key];
        const data = categoryData as FilterConfig;
        if (!categoryData || data?.type === 'fetchableCheckbox' || data?.type === 'date') return;
        if (categoryData.childFilters && Object.keys(categoryData.childFilters).length > 0) ids.add(key);
        else if (_.isObject(categoryData) && !(data?.options || data?.operations) && Object.keys(categoryData).length > 0) ids.add(key);
      });
    };
    check(filtersConfig.idea);
    check(filtersConfig.evidence);
    return ids;
  }, [filtersConfig]);

  // Handle category selection (toggle: click same parent again to close dropdown)
  const handleCategorySelectChild = (categoryId: string, categoryType: 'idea' | 'evidence') => {
    if (categoryId === selectedCategory) {
      setSelectedCategory('');
      setSelectedChildFilter(null);
      // Don't reset displayedCategory/displayedChildFilter on toggle close - keep showing last filter
      setMiddlePanelSearch('');
      setRightPanelSearch('');
      return;
    }

    setSelectedCategory(categoryId);
    setMiddlePanelSearch('');
    setRightPanelSearch('');

    // Only update displayed state and clear selectedChildFilter if the category does NOT have children
    // This keeps the last filter displayed in RightPanel when clicking on a parent
    if (!categoryIdsWithChildren.has(categoryId)) {
      setSelectedChildFilter(null);
      setDisplayedCategory(categoryId);
      setDisplayedChildFilter(null);
      setDisplayedCategoryType(categoryType);
    }
  };

  // Handle category selection (toggle: click same parent again to close dropdown)
  const handleCategorySelect = (categoryId: string, categoryType: 'idea' | 'evidence') => {
    if (categoryId === selectedCategory) {
      setSelectedCategory('');
      setSelectedChildFilter(null);
      // Don't reset displayedCategory/displayedChildFilter on toggle close - keep showing last filter
      setMiddlePanelSearch('');
      setRightPanelSearch('');
      return;
    }

    setSelectedCategory(categoryId);
    setMiddlePanelSearch('');
    setRightPanelSearch('');

    // Only update displayed state and clear selectedChildFilter if the category does NOT have children
    // This keeps the last filter displayed in RightPanel when clicking on a parent
    if (!categoryIdsWithChildren.has(categoryId)) {
      setSelectedChildFilter(null);
      setDisplayedCategory(categoryId);
      setDisplayedChildFilter(null);
      setDisplayedCategoryType(categoryType);
    }
  };

  // Handle child filter selection
  const handleChildFilterSelect = (childFilterId: string) => {
    setSelectedChildFilter(childFilterId);
    setDisplayedCategory(selectedCategory);
    setDisplayedChildFilter(childFilterId);
    setRightPanelSearch('');
  };

  // Apply filters to parent immediately (Approach A: every change is applied, modal stays open)
  const applyFilters = useCallback(
    (arg: ActiveFilter[] | ((prev: ActiveFilter[]) => ActiveFilter[])) => {
      if (typeof arg === 'function') {
        setLocalActiveFilters((prev) => {
          const next = (arg as (prev: ActiveFilter[]) => ActiveFilter[])(prev);
          setActiveFilters(next);
          return next;
        });
      } else {
        setLocalActiveFilters(arg);
        setActiveFilters(arg);
      }
    },
    [setActiveFilters]
  );

  const handleModalClose = useCallback(() => {
    setIsManagingFilters(false);
    onFiltersSaved?.();
    onClose();
  }, [onClose, onFiltersSaved]);

  // Clear all active filters, including page/view default filters and global
  // (owner/productArea/dateRange) filters — "Clear all" always clears everything.
  const handleClearAll = () => {
    const preserved: ActiveFilter[] = [];

    setLocalActiveFilters(preserved);
    setActiveFilters(preserved);

    setActiveOptions({});

    setFetchableCheckboxCounts({});
    setSelectedCategory('');
    setSelectedChildFilter(null);
    setDisplayedCategory('');
    setDisplayedChildFilter(null);
    setDisplayedCategoryType(null);
    setMiddlePanelSearch('');
    setRightPanelSearch('');
    setClearTrigger(prev => prev + 1);
    setAdditionalConfigurationsValues({});
    onClearFilters?.(preserved);
  };

  // Handle remove individual filter (SavedFilters row "Clear")
  const handleRemoveFilter = (filterName: string) => {
    setLocalActiveFilters((prev) => {
      const next = prev.filter((value: any) => value.name !== filterName);
      setActiveFilters(next);
      return next;
    });
    setClearTrigger((prev) => prev + 1);
  };


  const activeFilterCount = localActiveFilters.length;

  const hasActiveFilters = () => {
    return activeFilterCount > 0;
  };

  // "Clear all" always clears every active filter, including page/view defaults,
  // so it's clearable whenever there's anything active — no pinned-filter gating.
  const hasClearableFilters = true;

  const hasChildActiveFilters = () => {
    const currentName = selectedChildFilter || selectedCategory;
    const filteredActiveFilters = localActiveFilters.filter((item: any) => item.name === currentName);
    return filteredActiveFilters.length > 0;
  };

  // Get category icon based on category id
  const getCategoryIcon = (categoryId: string) => {
    // Handle Idea attributes category
    if (categoryId === 'idea') {
      return <IconCards size={16} />; // Default icon for idea attributes
    }

    // Handle evidence categories
    if (!filtersConfig?.evidence || !filtersConfig.evidence[categoryId]) {
      return <IconList size={16} />;
    }

    const categoryData = filtersConfig.evidence[categoryId];
    const iconName = categoryData.icon;

    if (!iconName) {
      return <IconList size={16} />;
    }

    // Get the mapped icon name from constants
    const mappedIconName = ICON_MAP[iconName as keyof typeof ICON_MAP] || ICON_MAP[iconName.toLowerCase() as keyof typeof ICON_MAP];

    if (!mappedIconName) {
      return <IconList size={16} />;
    }

    // Map icon names to available icon components
    const iconComponentMap: { [key: string]: any } = {
      'IconCalendar': IconCalendar,
      'IconTag': IconTag,
      'IconBuilding': IconBuilding,
      'IconTarget': IconTarget,
      'IconTicket': IconTicket,
      'IconMessageCircle': IconMessageCircle,
      'IconArticle': IconArticle,
      'IconUser': IconUser,
      'IconList': IconList
    };

    const IconComponent = iconComponentMap[mappedIconName];
    if (IconComponent) {
      return <IconComponent size={16} />;
    }

    return <IconList size={16} />;
  };

  // Get category tooltip based on category id
  const getCategoryTooltip = (categoryId: string, disabled= false) => {
    if(disabled){
      return 'Idea attributes can only be edited on the Ideas page.';
    }
    return `Filter by ${categoryId} attributes and properties.`;
  };

  const searchInput = (callback: any) => (
    <TextInput
      placeholder="Search..."
      value={middlePanelSearch}
      onChange={(e) => callback(e.currentTarget.value)}
      icon={<IconSearch size={15} color="#1C1B1F"/>}
      mb={16}
      styles={{
        input: {
          height: '32px',
          minHeight: '32px',
          border: '1px solid #D8D8DB',
          borderRadius: '4px',
          color: '#585C68',
          fontSize: '12px',
        },
      }}
    />
  );

  const ideaCategories = useMemo(() => {
    return _.filter(availableCategories, item => item.categoryType === 'idea');
  }, [availableCategories]);

  const evidenceCategories = useMemo(() => {
    const base = _.filter(availableCategories, item => item.categoryType === 'evidence');
    const ids = new Set(base.map(c => c.id));
    const extra: FilterCategory[] = [];

    // Only inject owner/product-area into the evidence column for evidence-only modals; on Ideas (idea | both) they are listed under Idea filters.
    const evidenceConfig = filtersConfig?.evidence;
    if (evidenceConfig && filterObject === 'evidence') {
      const addIfMissing = (key: string, category: FilterConfig | undefined) => {
        if (!category || ids.has(key)) return;
        ids.add(key);
        extra.push({
          id: key,
          label: category.label || key,
          active: selectedCategory === key,
          filterCount: getActiveFilterCountForCategory(key, localActiveFilters, 'evidence'),
          disabled: disabledCategories.includes(key),
          categoryType: 'evidence',
        });
      };
      addIfMissing('owner', evidenceConfig['owner']);
      addIfMissing('product-area', evidenceConfig['product-area']);
    }

    return [...base, ...extra];
  }, [availableCategories, filtersConfig, filterObject, selectedCategory, localActiveFilters, disabledCategories]);

  const manageIdeaCategories = useMemo(() => {
    return _.filter(allAvailableCategories, item => item.categoryType === 'idea');
  }, [allAvailableCategories]);

  const manageEvidenceCategories = useMemo(() => {
    return _.filter(allAvailableCategories, item =>
      item.categoryType === 'evidence' && !HIDDEN_FROM_MANAGE_IDS.has(item.id)
    );
  }, [allAvailableCategories]);

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      size={680}
      centered
      withCloseButton={false}
      lockScroll={false}
      overlayProps={{
        opacity: 0.62,
        blur: 0.04,
      }}
      styles={{
        header: {
          background: '#fff',
          padding: '16px 24px',
          width: '100%',
          borderBottom: '1px solid #e9ecef',
        },
        title: { width: '100%' },
        body: {
          padding: 0,
          overflow: 'hidden',
        },
        content: {
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '70vh',
          minHeight: 400,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Box px={24}>
            <FilterModalHeader
              onClearAll={handleClearAll}
              hasActiveFilters={hasActiveFilters}
              hasClearableFilters={hasClearableFilters}
              onClose={handleModalClose}
            />
          </Box>
          <SavedFilters
            filters={localActiveFilters}
            viewFilters={selectedView?.filters}
            filtersConfig={filtersConfig}
            onRemoveFilter={handleRemoveFilter}
            onSelectFilter={(filter) => {
              // Reset search states
              setMiddlePanelSearch('');
              setRightPanelSearch('');

              // Set the category type based on the filter's filterObject
              const filterObject = filter.filterObject as 'idea' | 'evidence';
              setDisplayedCategoryType(filterObject);

              // Look up the filter in filtersConfig to determine if it's a top-level or child filter
              // Check if filter.name exists as a top-level key in the filter's own config section
              const configSection = filterObject === 'idea' ? filtersConfig?.idea : filtersConfig?.evidence;
              
              const isTopLevelFilter = configSection?.[filter.name] !== undefined;

              // If filter has parent with '.', it's a nested child filter
              const hasNestedParent = filter.parent?.includes('.');
              
              // If filter has a distinct parent (parent !== name), it's a child filter
              const hasDistinctParent = filter.parent && filter.parent !== filter.name;

              if (hasNestedParent) {
                // Nested child filter (e.g., parent="source.type")
                // Extract the top-level parent (part before the first '.')
                const topLevelParent = filter.parent?.split('.')[0] || '';
                
                setSelectedCategory(topLevelParent);
                setSelectedChildFilter(filter.name);
                setDisplayedCategory(topLevelParent);
                setDisplayedChildFilter(filter.name);
              } else if (hasDistinctParent && !isTopLevelFilter) {
                // Child filter with distinct parent that's not also a top-level filter
                setSelectedCategory(filter.parent || '');
                setSelectedChildFilter(filter.name);
                setDisplayedCategory(filter.parent || '');
                setDisplayedChildFilter(filter.name);
              } else {
                // Top-level filter (exists directly in config, or parent === name, or no parent)
                // For top-level filters, selectedChildFilter should be null
                const categoryName = filter.name;
                setSelectedCategory(categoryName);
                setSelectedChildFilter(null);
                setDisplayedCategory(categoryName);
                setDisplayedChildFilter(null);
              }
            }}
          />
        </Box>
        <Box px={24} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {isSavingFilters ? (
            <Box className="filter-modal-container">
              <Box className="filter-modal-container__left" pr={16} py={12}>
                <Stack spacing={12}>
                  <Skeleton height={14} width="40%" radius="sm" />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={14} width="50%" radius="sm" mt={8} />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={38} radius="sm" />
                  <Skeleton height={38} radius="sm" />
                </Stack>
              </Box>
              <Box className="filter-modal-container__right" pl={16} py={12} />
            </Box>
          ) : isManagingFilters ? (
            <ManageFilterModalContainer
              ideaCategories={manageIdeaCategories}
              evidenceCategories={manageEvidenceCategories}
              getCategoryTooltip={getCategoryTooltip}
              getCategoryIcon={getCategoryIcon}
              handleCategorySelect={handleCategorySelect}
              selectedCategory={selectedCategory}
              childFilters={childFilters}
              categoryIdsWithChildren={categoryIdsWithChildren}
              searchInput={searchInput(setMiddlePanelSearch)}
              handleChildFilterSelect={handleChildFilterSelect}
              middlePanelSearch={middlePanelSearch}
              selectedChildFilter={selectedChildFilter}
              filtersConfig={filtersConfig}
              localActiveFilters={localActiveFilters}
              setLocalActiveFilters={applyFilters}
              getFilterKey={getFilterKey}
              setFetchableCheckboxCounts={setFetchableCheckboxCounts}
              clearTrigger={clearTrigger}
              isFromIdeas={isFromIdeas}
              filterVisibility={filterVisibility}
              onToggleVisibility={handleToggleVisibility}
            />
          ) : (
            <FilterModalContainer
              ideaCategories={ideaCategories}
              evidenceCategories={evidenceCategories}
              getCategoryTooltip={getCategoryTooltip}
              getCategoryIcon={getCategoryIcon}
              handleCategorySelect={handleCategorySelect}
              selectedCategory={selectedCategory}
              childFilters={childFilters}
              categoryIdsWithChildren={categoryIdsWithChildren}
              searchInput={searchInput(setMiddlePanelSearch)}
              handleChildFilterSelect={handleChildFilterSelect}
              middlePanelSearch={middlePanelSearch}
              selectedChildFilter={selectedChildFilter}
              handleRemoveFilter={handleRemoveFilter}
              hasChildActiveFilters={hasChildActiveFilters}
              additionalConfigurations={additionalConfigurations}
              additionalConfigurationsValues={additionalConfigurationsValues}
              setAdditionalConfigurationsValues={setAdditionalConfigurationsValues}
              filtersConfig={filtersConfig}
              localActiveFilters={localActiveFilters}
              setLocalActiveFilters={applyFilters}
              getFilterKey={getFilterKey}
              setFetchableCheckboxCounts={setFetchableCheckboxCounts}
              clearTrigger={clearTrigger}
              isFromIdeas={isFromIdeas}
              displayedCategory={displayedCategory}
              displayedChildFilter={displayedChildFilter}
              displayedCategoryType={displayedCategoryType}
            />
          )}
        </Box>
        <ManageFiltersSelection
          isManagingFilters={isManagingFilters}
          setIsManagingFilters={setIsManagingFilters}
          editFilters={editFilters}
        />
      </Box>
    </Modal>
  );
};

export default ReusableFilterModal;